/**
 * Фильтры задачника — одни правила для веба и приложения (§16.3, §16.4).
 *
 * Где хранится состояние, решает клиент: веб — в адресе страницы (ссылку
 * можно переслать коллеге), приложение — в параметрах маршрута. Здесь —
 * что считается фильтром, как его проверить и во что он превращается
 * в запросе tasks.list.
 */

import type { TaskFilters } from "./api";

export type TaskStateFilter = "" | "open" | "done";

export interface TaskFilterState {
  search: string;
  assigneeIds: string[];
  priorityIds: string[];
  typeIds: string[];
  tagIds: string[];
  dueFrom: string | null;
  dueTo: string | null;
  overdueOnly: boolean;
  mineOnly: boolean;
  /** «Мои сегодня»: мои задачи со сроком сегодня и мои просроченные. */
  myToday: boolean;
  /** Открытые или готовые задачи; пустая строка — все. */
  state: TaskStateFilter;
}

/**
 * Значение фильтра исполнителей «Без исполнителя» — рядом с идентификаторами
 * людей, в том же списке: вместе с ними даёт «их задачи или ничьи».
 * Сервер знает его под тем же именем (tasks/reads.py, NO_ASSIGNEE).
 */
export const NO_ASSIGNEE = "none";
export const NO_ASSIGNEE_LABEL = "Без исполнителя";

export const EMPTY_FILTERS: TaskFilterState = {
  search: "",
  assigneeIds: [],
  priorityIds: [],
  typeIds: [],
  tagIds: [],
  dueFrom: null,
  dueTo: null,
  overdueOnly: false,
  mineOnly: false,
  myToday: false,
  state: "",
};

export interface FilterValidation {
  /** «с» позже «до» — выборка заведомо пуста (§16.3). */
  invalidRange: boolean;
}

const ISO_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Проверка даты по существу, а не по форме: «2026-13-45» имеет верный вид,
 * но такого месяца и дня нет. Отправлять это на сервер нельзя.
 */
export function isValidIsoDate(value: string): boolean {
  if (!ISO_SHAPE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toISOString().slice(0, 10) === value;
}

export function parseTaskState(value: string | null | undefined): TaskStateFilter {
  return value === "open" || value === "done" ? value : "";
}

export function validateFilters(filters: TaskFilterState): FilterValidation {
  return {
    invalidRange: filters.dueFrom !== null && filters.dueTo !== null && filters.dueFrom > filters.dueTo,
  };
}

/** Сколько фильтров включено — для «Фильтры · N». Период — один фильтр. */
export function countActiveFilters(filters: TaskFilterState): number {
  let count = 0;
  if (filters.search !== "") count += 1;
  count += filters.assigneeIds.length > 0 ? 1 : 0;
  count += filters.priorityIds.length > 0 ? 1 : 0;
  count += filters.typeIds.length > 0 ? 1 : 0;
  count += filters.tagIds.length > 0 ? 1 : 0;
  if (filters.dueFrom !== null || filters.dueTo !== null) count += 1;
  if (filters.overdueOnly) count += 1;
  if (filters.mineOnly) count += 1;
  if (filters.myToday) count += 1;
  if (filters.state !== "") count += 1;
  return count;
}

const STATE_PARAM = { open: "OPEN", done: "DONE" } as const;

/**
 * Что значит период: на доске — срок задачи; в списке задач подразделения —
 * период работы, по правилу «Отчёта по работам» (выполнены в периоде или
 * были в работе), чтобы список и отчёт за тот же период совпадали.
 */
export type PeriodMode = "due" | "work";

/**
 * Фильтры → параметры tasks.list. Пустой диапазон (§16.3) не отправляется:
 * сервер вернул бы пустой список, а причина осталась бы неясной.
 */
export function filtersToQuery(
  filters: TaskFilterState,
  period: PeriodMode = "due",
): Omit<TaskFilters, "boardId" | "departmentId"> {
  const { invalidRange } = validateFilters(filters);
  const from = period === "work" ? "workFrom" : "dueFrom";
  const to = period === "work" ? "workTo" : "dueTo";
  return {
    ...(filters.search === "" ? {} : { search: filters.search }),
    ...(filters.assigneeIds.length === 0 ? {} : { assigneeIds: filters.assigneeIds }),
    ...(filters.priorityIds.length === 0 ? {} : { priorityIds: filters.priorityIds }),
    ...(filters.typeIds.length === 0 ? {} : { typeIds: filters.typeIds }),
    ...(filters.tagIds.length === 0 ? {} : { tagIds: filters.tagIds }),
    ...(filters.dueFrom === null || invalidRange ? {} : { [from]: filters.dueFrom }),
    ...(filters.dueTo === null || invalidRange ? {} : { [to]: filters.dueTo }),
    ...(filters.overdueOnly ? { overdueOnly: true } : {}),
    ...(filters.mineOnly || filters.myToday ? { mineOnly: true } : {}),
    ...(filters.myToday ? { dueTodayOrOverdue: true } : {}),
    ...(filters.state === "" ? {} : { state: STATE_PARAM[filters.state] }),
  };
}
